/**
 * Ordering rule for exchange-API template version labels (`MAJOR.MINOR.PATCH`).
 *
 * «Guardar como nueva versión» must pick a label STRICTLY GREATER than every
 * label the template already carries — its saved snapshots AND the live row's
 * own version. The backend refuses anything else with 409
 * `TEMPLATE_VERSION_NOT_MONOTONIC`; the editor dialog disables its submit with
 * the same rule so the refusal is shown before any request leaves the browser.
 *
 * One definition, read by both sides: when the two sides disagreed, the dialog
 * enabled a version the server then refused, AFTER the live row had already
 * been rewritten by a separate PUT.
 *
 * Comparison is a numeric tuple comparison (so `1.10.0 > 1.9.0`). Labels that
 * are not strict `MAJOR.MINOR.PATCH` are ignored for ordering: they cannot be
 * ordered, and legacy rows may hold them.
 */

const TEMPLATE_VERSION_PATTERN = /^(\d+)\.(\d+)\.(\d+)$/u

/** True for a strict `MAJOR.MINOR.PATCH` label (no pre-release / build tags). */
export function isTemplateVersionLabel(version: string): boolean {
  return TEMPLATE_VERSION_PATTERN.test(version)
}

function parseTemplateVersionLabel(version: string): [number, number, number] | null {
  const match = TEMPLATE_VERSION_PATTERN.exec(version)
  if (!match) return null
  return [Number(match[1]), Number(match[2]), Number(match[3])]
}

/**
 * Negative when `a < b`, positive when `a > b`, zero when equal. Returns `null`
 * when either side is not a strict `MAJOR.MINOR.PATCH` label.
 */
export function compareTemplateVersionLabels(a: string, b: string): number | null {
  const left = parseTemplateVersionLabel(a)
  const right = parseTemplateVersionLabel(b)
  if (left === null || right === null) return null
  for (let index = 0; index < 3; index += 1) {
    if (left[index] !== right[index]) return left[index] - right[index]
  }
  return 0
}

/**
 * The highest orderable label among `versions`, or `null` when none is
 * orderable. `null`/`undefined` entries are skipped so callers can pass
 * optional values (e.g. a live version that has not loaded yet).
 */
export function highestTemplateVersionLabel(
  versions: ReadonlyArray<string | null | undefined>,
): string | null {
  let highest: string | null = null
  for (const candidate of versions) {
    if (typeof candidate !== 'string' || !isTemplateVersionLabel(candidate)) continue
    if (highest === null || (compareTemplateVersionLabels(candidate, highest) ?? 0) > 0) {
      highest = candidate
    }
  }
  return highest
}

/**
 * The existing label a new version must exceed and does not, or `null` when
 * `candidate` is strictly greater than every orderable label in `existing`.
 *
 * The returned label is the HIGHEST existing one, so a message built from it
 * names the version the user actually has to go above.
 */
export function findTemplateVersionNotExceeded(
  candidate: string,
  existing: ReadonlyArray<string | null | undefined>,
): string | null {
  const highest = highestTemplateVersionLabel(existing)
  if (highest === null) return null
  const comparison = compareTemplateVersionLabels(candidate, highest)
  if (comparison === null) return null
  return comparison > 0 ? null : highest
}
